import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import XSifterQueryService, { TWEET_HTML_UNAVAILABLE_MESSAGE } from "../../services/XSifterQueryService";
import { isAllOf } from "@reduxjs/toolkit";

export const RELEVANT_LABEL = "relevant";
export const NOT_RELEVANT_LABEL = "not relevant";

interface IXSifterCodableItemProps {
    itemId: string;
    itemUsername: string;
    currentLabel: string | null;
    codingRoundId: string;
    onMarkRelevant: (itemId: string, codingRoundId: string) => void;
    onMarkNotRelevant: (itemId: string, codingRoundId: string) => void;
    onRecodeItem: (itemId: string, codingRoundId: string) => void;
    onItemUnavailable: (itemId: string, codingRoundId: string) => void;
    onTweetLoaded?: () => void;
    onTweetHtmlResolved?: (itemId: string, tweetHtml: string) => void;
    tweetText?: string | undefined;
    tweetUsername?: string | undefined;
    tweetDisplayName?: string | undefined;
    cachedTweetHtml?: string | undefined;
    showPreviewOnly?: boolean;
}

const XSifterCodableItem = React.forwardRef<HTMLDivElement, IXSifterCodableItemProps>((props, ref) => {
    const {
        itemId,
        itemUsername,
        currentLabel,
        codingRoundId,
        tweetText,
        tweetUsername,
        tweetDisplayName,
        showPreviewOnly,
        onMarkRelevant,
        onMarkNotRelevant,
        onRecodeItem,
        onItemUnavailable,
        onTweetLoaded,
        onTweetHtmlResolved,
        cachedTweetHtml,
    } = props;
    const [tweetHtml, setTweetHtml] = useState<string>(cachedTweetHtml ?? "");
    const [isLoadingTweet, setIsLoadingTweet] = useState<boolean>(!cachedTweetHtml);
    const isLabeled = !!currentLabel;
    const showPlainPreview = isLabeled || !!showPreviewOnly;

    useEffect(() => {
        if (showPlainPreview) {
            return;
        }

        if (tweetHtml) {
            return;
        }

        setIsLoadingTweet(true);
        (new XSifterQueryService()).getRenderedTweetHtml(itemId, itemUsername)
            .then((html) => {
                if (!html || html === TWEET_HTML_UNAVAILABLE_MESSAGE) {
                    onItemUnavailable(itemId, codingRoundId);
                    return;
                }
                setTweetHtml(html);
                onTweetHtmlResolved?.(itemId, html);
            })
            .finally(() => {
                setIsLoadingTweet(false);
            });
    }, [showPlainPreview, itemId, itemUsername, tweetHtml]);
    
    useEffect(() => {
        if (!isLoadingTweet && tweetHtml) {
            const twttr = (window as any).twttr;
            if (twttr && typeof twttr.widgets.load === 'function') {
                twttr.widgets.load();
            } else {
                // If the script isn't on the page yet, dynamically inject it once
                const script = document.createElement('script');
                script.setAttribute('src', 'https://twitter.com');
                script.setAttribute('charset', 'utf-8');
                script.setAttribute('async', 'true');
                document.head.appendChild(script);
            }
            onTweetLoaded?.();
    }}, [tweetHtml, isLoadingTweet])

    return (
        <>
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    minHeight: showPlainPreview ? "" : 150,
                    display: "flex",
                    alignItems: showPlainPreview ? "flex-start" : "center",
                    justifyContent: showPlainPreview ? "flex-start" : "center",
                    flexDirection: "column",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 5,
                    p: 2,
                    backgroundColor: isLabeled ? "#dddddd" : "#ffffff"
                }}
            >
                {showPlainPreview ? (
                    <>
                        <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700 }}>{tweetDisplayName}</Box>
                            {tweetUsername && (
                                <Box component="span" sx={{ color: "text.secondary" }}> (@{tweetUsername})</Box>
                            )}
                        </Typography>
                        <Box
                            sx={{
                                mt: 1,
                                p: 1.5,
                                width: "100%",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                backgroundColor: "#f5f5f5",
                                boxSizing: "border-box",
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {tweetText}
                            </Typography>
                        </Box>
                        {isLabeled && (
                            <Button
                                variant="contained"
                                color={currentLabel === RELEVANT_LABEL ? "success" : "error"}
                                onClick={() => onRecodeItem(itemId, codingRoundId)}
                                sx={{
                                    mt: 1.5,
                                    borderRadius: 999,
                                    minHeight: 0,
                                    lineHeight: 1.4,
                                    py: 0.25,
                                    px: 1.5,
                                    fontSize: "0.75rem",
                                    textTransform: "capitalize",
                                }}
                            >
                                {currentLabel}
                            </Button>
                        )}
                    </>
                ) : isLoadingTweet ? (
                    <CircularProgress size={24} />
                ) : tweetHtml ? (
                    <div id="tweet-container" dangerouslySetInnerHTML={{ __html: tweetHtml }} />
                ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        (tweet {itemId} placeholder)
                    </Typography>
                )}
            </Box>
            {!isLabeled && !showPreviewOnly && (
                <Stack ref={ref} direction="row" spacing={1} sx={{ mt: 1, justifyContent: "center", marginBottom: "16px" }}>
                    <Button
                        variant="outlined"
                        color="success"
                        startIcon={<ThumbUpIcon />}
                        onClick={() => onMarkRelevant(itemId, codingRoundId)}
                        sx={{ borderRadius: 999 }}
                    >
                        Relevant
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<ThumbDownIcon />}
                        onClick={() => onMarkNotRelevant(itemId, codingRoundId)}
                        sx={{ borderRadius: 999 }}
                    >
                        Not Relevant
                    </Button>
                </Stack>
            )}
        </Box>
        </>
    )
});

XSifterCodableItem.displayName = "XSifterCodableItem";

export default XSifterCodableItem;
